import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';
import { Key, UserPlus, CheckSquare, Square, Users, Shield } from 'lucide-react';

const PermissionManager = () => {
  const [permissions, setPermissions] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleTemplates, setRoleTemplates] = useState({
    'Super Admin': ['dashboard.view', 'orders.view', 'orders.create', 'orders.update', 'orders.delete', 'products.view', 'products.create', 'products.update', 'products.delete', 'customers.view', 'inventory.view', 'inventory.update', 'reports.view', 'reports.create', 'settings.view', 'settings.update', 'security.view', 'security.manage', 'analytics.view'],
    'Admin': ['dashboard.view', 'orders.view', 'orders.update', 'products.view', 'products.update', 'customers.view', 'inventory.view', 'reports.view', 'analytics.view'],
    'Manager': ['dashboard.view', 'orders.view', 'products.view', 'customers.view', 'reports.view'],
    'Viewer': ['dashboard.view', 'orders.view', 'products.view', 'customers.view']
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;

      const [permsRes, usersRes] = await Promise.all([
        fetch(`${baseUrl}/api/admin/security/permissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${baseUrl}/api/admin/security/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (permsRes.ok) {
        const permsData = await permsRes.json();
        setPermissions(permsData.permissions || []);
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setAdminUsers(usersData.users || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = (user) => {
    setSelectedUser(user);
    setUserPermissions(user.permissions || []);
  };

  const togglePermission = (permissionId) => {
    if (userPermissions.includes(permissionId)) {
      setUserPermissions(userPermissions.filter(p => p !== permissionId));
    } else {
      setUserPermissions([...userPermissions, permissionId]);
    }
  };

  const applyRoleTemplate = (templateName) => {
    if (roleTemplates[templateName]) {
      setUserPermissions(roleTemplates[templateName]);
    }
  };

  const saveUserPermissions = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/security/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions: userPermissions })
      });

      if (response.ok) {
        alert('Permissions updated successfully');
        fetchData();
        setSelectedUser(null);
      } else {
        alert('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      alert('Error updating permissions');
    }
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Key className="h-6 w-6 mr-2 text-[#d44100]" />
          Permission Management
        </h2>
        <p className="text-sm text-gray-600 mt-1">Manage user permissions and access control</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Admin Users
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {adminUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => loadUserPermissions(user)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedUser?.id === user.id
                    ? 'border-[#d44100] bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">{user.fullName}</div>
                <div className="text-sm text-gray-600">{user.email}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {user.permissions?.length || 0} permissions
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Permissions */}
        {selectedUser && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Permissions for {selectedUser.fullName}
                </h3>
                <p className="text-sm text-gray-600">{selectedUser.email}</p>
              </div>
              <div className="flex space-x-2">
                <select
                  onChange={(e) => applyRoleTemplate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  defaultValue=""
                >
                  <option value="">Apply Role Template</option>
                  {Object.keys(roleTemplates).map((template) => (
                    <option key={template} value={template}>{template}</option>
                  ))}
                </select>
                <button
                  onClick={saveUserPermissions}
                  className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] text-sm"
                >
                  Save Permissions
                </button>
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(groupedPermissions).map(([module, modulePerms]) => (
                <div key={module} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    {module}
                  </h4>
                  <div className="space-y-2">
                    {modulePerms.map((perm) => (
                      <label
                        key={perm.id}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={userPermissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="mr-3 h-4 w-4 text-[#d44100] focus:ring-[#d44100]"
                        />
                        <span className="text-sm text-gray-700">{perm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!selectedUser && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Select a user to manage their permissions</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionManager;







